// DeleteConfigOption — trash button + inline two-step confirmation for one system_config row.
// The parent only renders this for inactive rows (disable first, then delete); the API is the
// authority on whether the option is still in use and returns the message shown on a 409.
import { useState } from "react";
import type { ConfigCategory } from "@dm-lms/shared/types";
import { deleteJson } from "../../hooks/useApi.js";
import { invalidateConfig } from "../../hooks/useConfig.js";

interface DeleteConfigOptionProps {
  id:        string;
  label:     string;
  category:  ConfigCategory;
  onDeleted: (id: string) => void;
}

export default function DeleteConfigOption({ id, label, category, onDeleted }: DeleteConfigOptionProps) {
  const [confirming, setConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function cancel() {
    setConfirming(false);
    setError(null);
  }

  async function confirm() {
    setIsDeleting(true);
    setError(null);
    try {
      const res = await deleteJson<{ id: string }>(`/api/config/${id}`);
      if (!res.ok) {
        setError(res.error);
        setIsDeleting(false);
        return;
      }
      invalidateConfig(category);
      onDeleted(id);   // parent drops the row, which unmounts this component
    } catch {
      setError("Unable to delete option. Please try again.");
      setIsDeleting(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        aria-label={`Delete ${label}`}
        title="Delete"
        onClick={() => { setConfirming(true); }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-red-300 text-red-600 transition hover:bg-red-50"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
        </svg>
      </button>
    );
  }

  return (
    <div role="alert" className="max-w-xs rounded-md border border-red-200 bg-red-50 p-3 text-sm">
      <p className="text-dm-navy">Delete {label}? This cannot be undone.</p>
      {error && <p className="mt-2 text-red-600">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={isDeleting}
          onClick={() => { void confirm(); }}
          className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
        <button
          type="button"
          disabled={isDeleting}
          onClick={cancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-dm-slate transition hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
